// Clevor's icon set: Phosphor, duotone weight, exported under the names the
// code already used with Heroicons. One place to change the look; the pages
// never import an icon library directly.
//
// Duotone draws a second, 20%-opacity layer in currentColor, so an icon takes
// its two tones from one `text-*` class. Size comes from the Tailwind `h-*`/
// `w-*` classes as before (CSS wins over the SVG size attribute).
import React, { forwardRef } from 'react';
import type { Icon, IconProps, IconWeight } from '@phosphor-icons/react';
import * as P from '@phosphor-icons/react';

type Props = IconProps & React.RefAttributes<SVGSVGElement>;

function withWeight(Base: Icon, weight: IconWeight) {
  const Wrapped = forwardRef<SVGSVGElement, Props>(function ClevorIcon(props, ref) {
    return <Base ref={ref} weight={weight} {...props} />;
  });
  Wrapped.displayName = `${Base.displayName ?? 'Icon'}(${weight})`;
  return Wrapped;
}


export const AcademicCapIcon = withWeight(P.GraduationCap, 'duotone');
export const AdjustmentsHorizontalIcon = withWeight(P.SlidersHorizontal, 'duotone');
export const ArrowDownTrayIcon = withWeight(P.DownloadSimple, 'duotone');
export const ArrowLeftIcon = withWeight(P.ArrowLeft, 'duotone');
export const ArrowPathIcon = withWeight(P.ArrowsClockwise, 'duotone');
export const ArrowUturnLeftIcon = withWeight(P.ArrowUUpLeft, 'duotone');
export const ArrowRightCircleIcon = withWeight(P.ArrowCircleRight, 'duotone');
export const ArrowRightIcon = withWeight(P.ArrowRight, 'duotone');
export const ArrowTopRightOnSquareIcon = withWeight(P.ArrowSquareOut, 'duotone');
export const ArrowsPointingInIcon = withWeight(P.ArrowsIn, 'duotone');
export const ArrowsPointingOutIcon = withWeight(P.ArrowsOut, 'duotone');
export const Bars3Icon = withWeight(P.List, 'duotone');
export const BellAlertIcon = withWeight(P.BellRinging, 'duotone');
export const BellIcon = withWeight(P.Bell, 'duotone');
export const BellSlashIcon = withWeight(P.BellSlash, 'duotone');
export const BookOpenIcon = withWeight(P.BookOpen, 'duotone');
export const BookmarkIcon = withWeight(P.BookmarkSimple, 'duotone');
export const BriefcaseIcon = withWeight(P.Briefcase, 'duotone');
export const BuildingLibraryIcon = withWeight(P.Bank, 'duotone');
export const BuildingOffice2Icon = withWeight(P.Buildings, 'duotone');
export const BuildingOfficeIcon = withWeight(P.Building, 'duotone');
export const CalendarDaysIcon = withWeight(P.CalendarDots, 'duotone');
export const CalendarIcon = withWeight(P.Calendar, 'duotone');
export const ChartBarIcon = withWeight(P.ChartBar, 'duotone');
export const ChatBubbleLeftIcon = withWeight(P.ChatCircle, 'duotone');
export const ChatBubbleLeftRightIcon = withWeight(P.ChatsCircle, 'duotone');
export const CheckBadgeIcon = withWeight(P.SealCheck, 'duotone');
export const CheckCircleIcon = withWeight(P.CheckCircle, 'duotone');
export const CheckIcon = withWeight(P.Check, 'duotone');
export const ChevronDownIcon = withWeight(P.CaretDown, 'duotone');
export const ChevronLeftIcon = withWeight(P.CaretLeft, 'duotone');
export const ChevronRightIcon = withWeight(P.CaretRight, 'duotone');
export const ChevronUpDownIcon = withWeight(P.CaretUpDown, 'duotone');
export const ChevronUpIcon = withWeight(P.CaretUp, 'duotone');
export const ClipboardDocumentCheckIcon = withWeight(P.ListChecks, 'duotone');
export const ClipboardDocumentIcon = withWeight(P.ClipboardText, 'duotone');
export const ClipboardDocumentListIcon = withWeight(P.ListDashes, 'duotone');
export const ClockIcon = withWeight(P.Clock, 'duotone');
export const CloudIcon = withWeight(P.Cloud, 'duotone');
export const CommandLineIcon = withWeight(P.Terminal, 'duotone');
export const CpuChipIcon = withWeight(P.Cpu, 'duotone');
export const CreditCardIcon = withWeight(P.CreditCard, 'duotone');
export const CurrencyDollarIcon = withWeight(P.CurrencyCircleDollar, 'duotone');
export const DevicePhoneMobileIcon = withWeight(P.DeviceMobile, 'duotone');
export const DocumentDuplicateIcon = withWeight(P.Copy, 'duotone');
export const DocumentIcon = withWeight(P.File, 'duotone');
export const DocumentMagnifyingGlassIcon = withWeight(P.FileMagnifyingGlass, 'duotone');
export const DocumentTextIcon = withWeight(P.FileText, 'duotone');
export const EnvelopeIcon = withWeight(P.EnvelopeSimple, 'duotone');
export const ExclamationTriangleIcon = withWeight(P.Warning, 'duotone');
export const EyeIcon = withWeight(P.Eye, 'duotone');
export const EyeSlashIcon = withWeight(P.EyeSlash, 'duotone');
export const FolderIcon = withWeight(P.Folder, 'duotone');
export const FunnelIcon = withWeight(P.Funnel, 'duotone');
export const GlobeAltIcon = withWeight(P.Globe, 'duotone');
export const HandRaisedIcon = withWeight(P.Hand, 'duotone');
export const IdentificationIcon = withWeight(P.IdentificationCard, 'duotone');
export const InformationCircleIcon = withWeight(P.Info, 'duotone');
export const KeyIcon = withWeight(P.Key, 'duotone');
export const LinkIcon = withWeight(P.Link, 'duotone');
export const LockClosedIcon = withWeight(P.Lock, 'duotone');
export const MagnifyingGlassIcon = withWeight(P.MagnifyingGlass, 'duotone');
export const MapPinIcon = withWeight(P.MapPin, 'duotone');
export const MicrophoneIcon = withWeight(P.Microphone, 'duotone');
export const PaperAirplaneIcon = withWeight(P.PaperPlaneTilt, 'duotone');
export const PencilIcon = withWeight(P.Pencil, 'duotone');
export const PencilSquareIcon = withWeight(P.PencilSimpleLine, 'duotone');
export const PhoneIcon = withWeight(P.Phone, 'duotone');
export const PlayIcon = withWeight(P.Play, 'duotone');
export const PlusCircleIcon = withWeight(P.PlusCircle, 'duotone');
export const PlusIcon = withWeight(P.Plus, 'duotone');
export const ScaleIcon = withWeight(P.Scales, 'duotone');
export const ShieldCheckIcon = withWeight(P.ShieldCheck, 'duotone');
export const ShieldExclamationIcon = withWeight(P.ShieldWarning, 'duotone');
export const SparklesIcon = withWeight(P.Sparkle, 'duotone');
export const StarIcon = withWeight(P.Star, 'duotone');
export const StopIcon = withWeight(P.Stop, 'duotone');
export const TableCellsIcon = withWeight(P.Table, 'duotone');
export const TagIcon = withWeight(P.Tag, 'duotone');
export const TrashIcon = withWeight(P.Trash, 'duotone');
export const TrophyIcon = withWeight(P.Trophy, 'duotone');
export const UserCircleIcon = withWeight(P.UserCircle, 'duotone');
export const UserGroupIcon = withWeight(P.UsersThree, 'duotone');
export const UserIcon = withWeight(P.User, 'duotone');
export const UsersIcon = withWeight(P.Users, 'duotone');
export const WrenchScrewdriverIcon = withWeight(P.Wrench, 'duotone');
export const XCircleIcon = withWeight(P.XCircle, 'duotone');
export const XMarkIcon = withWeight(P.X, 'duotone');
export const CompassIcon = withWeight(P.Compass, 'duotone');
export const KanbanIcon = withWeight(P.Kanban, 'duotone');
export const ScrollIcon = withWeight(P.Scroll, 'duotone');
export const GaugeIcon = withWeight(P.Gauge, 'duotone');
export const CrosshairIcon = withWeight(P.Crosshair, 'duotone');
export const HandshakeIcon = withWeight(P.Handshake, 'duotone');
export const BinocularsIcon = withWeight(P.Binoculars, 'duotone');
export const ChartLineUpIcon = withWeight(P.ChartLineUp, 'duotone');

export const CalculatorIcon = withWeight(P.Calculator, 'duotone');
export const CogIcon = withWeight(P.Gear, 'duotone');
export const HomeIcon = withWeight(P.House, 'duotone');
export const PuzzlePieceIcon = withWeight(P.PuzzlePiece, 'duotone');

export { withWeight };
